import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  AlertTriangle,
  Award,
  BarChart3,
  CalendarDays,
  ClipboardList,
  MapPin,
  Target,
  TrendingUp,
  UserRound,
} from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const maxScore = 90;

const sectionStyles = {
  Speaking: {
    panel: "bg-green-50 text-green-700 ring-green-100",
    chart: "rgba(74, 222, 128, 0.72)",
    border: "rgba(34, 197, 94, 1)",
  },
  Writing: {
    panel: "bg-blue-50 text-blue-700 ring-blue-100",
    chart: "rgba(96, 165, 250, 0.72)",
    border: "rgba(37, 99, 235, 1)",
  },
  Reading: {
    panel: "bg-orange-50 text-orange-700 ring-orange-100",
    chart: "rgba(251, 146, 60, 0.72)",
    border: "rgba(234, 88, 12, 1)",
  },
  Listening: {
    panel: "bg-purple-50 text-purple-700 ring-purple-100",
    chart: "rgba(168, 85, 247, 0.72)",
    border: "rgba(126, 34, 206, 1)",
  },
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
};

const formatNumber = (value, fallback = "N/A") => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Number.isInteger(number) ? number : number.toFixed(2);
};

const ReportCardLoading = () => (
  <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
    <div className="h-4 w-28 rounded-sm bg-purple-100" />
    <div className="mt-3 h-8 w-72 max-w-full rounded-sm bg-slate-200" />
    <div className="mt-6 grid gap-4 md:grid-cols-3">
      {[1, 2, 3].map((item) => (
        <div key={item} className="h-28 rounded-md bg-slate-50" />
      ))}
    </div>
    <div className="mt-6 h-80 rounded-md bg-slate-50" />
  </section>
);

const ReportCard = () => {
  const { id } = useParams();
  const [mockTest, setMockTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMockTestDataById = async () => {
      try {
        const response = await fetch(`/api/fetch-mocktestdata/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 404 || response.status === 204) {
          setMockTest(null);
          return;
        }

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        setMockTest(data || null);
      } catch (err) {
        setError("Error fetching mock test data by id");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMockTestDataById();
  }, [id]);

  if (loading) return <ReportCardLoading />;

  if (error) {
    return (
      <section className="rounded-md border border-red-100 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold uppercase text-red-500">
          Result unavailable
        </p>
        <h1 className="mt-2 text-2xl font-extrabold text-gray-900">
          We could not load this report card.
        </h1>
        <p className="mt-2 text-gray-600">{error}</p>
      </section>
    );
  }

  if (!mockTest) {
    return (
      <section className="rounded-md border border-slate-200 bg-white p-8 text-center shadow-sm">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-md bg-purple-50 text-purple-700">
          <Award className="h-6 w-6" aria-hidden="true" />
        </span>
        <h1 className="mt-4 text-2xl font-extrabold text-gray-900">
          Result not found
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          This mock test report may not be available yet.
        </p>
      </section>
    );
  }

  const reportTitle = mockTest.mockTestName || "Mock Test";
  const report = mockTest.mockReport || {};
  const analytics = mockTest.analytics || report.analytics || {};
  const questionTypeBreakdown =
    mockTest.questionTypeBreakdown || report.questionTypeBreakdown || {};
  const highImpactWeaknesses = analytics.highImpactWeaknesses || [];
  const recommendedPractice = analytics.recommendedPractice || [];
  const crossScoreLosses = analytics.crossScoreLosses || report.crossScoring || [];
  const taskBreakdownRows = Object.values(questionTypeBreakdown)
    .sort((left, right) => (left.averageScore || 0) - (right.averageScore || 0))
    .slice(0, 10);
  const sectionScores = mockTest.sectionScores || report.sectionScores || {};
  const allScores = [
    { section: "Speaking", value: Number(sectionScores.speaking) || 0 },
    { section: "Writing", value: Number(sectionScores.writing) || 0 },
    { section: "Reading", value: Number(sectionScores.reading) || 0 },
    { section: "Listening", value: Number(sectionScores.listening) || 0 },
  ];
  const sectionFromName = reportTitle.split(" ")[0];
  const namedSectionScore = allScores.find(
    (score) => score.section === sectionFromName
  );
  const scores =
    reportTitle === "Full Mock Test"
      ? allScores
      : namedSectionScore
        ? [namedSectionScore]
        : allScores.filter((score) => score.value > 0);

  const overallScore = Number(
    analytics.overallPrediction ||
      report.overallPrediction ||
      mockTest.overallPrediction ||
      mockTest.averageScore ||
      0
  );

  const barChartData = {
    labels: scores.map((score) => score.section),
    datasets: [
      {
        label: "Scores",
        data: scores.map((score) => score.value),
        backgroundColor: scores.map((score) => sectionStyles[score.section].chart),
        borderColor: scores.map((score) => sectionStyles[score.section].border),
        borderWidth: 1,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: maxScore,
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            {reportTitle}
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            PTE Report Card
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Weighted score prediction, task analytics, and result details from this mock attempt.
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-md bg-purple-50 p-3 text-purple-800 ring-1 ring-purple-100">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-white shadow-sm">
            <Award className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase">Overall score</p>
            <p className="text-2xl font-extrabold">{overallScore.toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
              <UserRound className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase text-gray-500">
                Student
              </p>
              <p className="text-base font-extrabold text-gray-900">
                {mockTest.username || "Student"}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
              <CalendarDays className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase text-gray-500">
                Test date
              </p>
              <p className="text-base font-extrabold text-gray-900">
                {formatDate(mockTest.updatedAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
              <MapPin className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase text-gray-500">
                Test center
              </p>
              <p className="text-base font-extrabold text-gray-900">
                PrepNexis Online Test
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-md bg-purple-50 text-purple-700">
            <BarChart3 className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Communicative Skills
            </p>
            <h2 className="text-xl font-extrabold text-gray-900">
              Section performance
            </h2>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {scores.map((score) => {
            const styles = sectionStyles[score.section];
            const pieChartData = {
              labels: ["Score", "Remaining"],
              datasets: [
                {
                  data: [score.value, Math.max(maxScore - score.value, 0)],
                  backgroundColor: [styles.chart, "rgba(226, 232, 240, 0.9)"],
                  borderColor: [styles.border, "rgba(203, 213, 225, 1)"],
                  borderWidth: 1,
                },
              ],
            };

            return (
              <article
                key={score.section}
                className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-extrabold text-gray-900">
                      {score.section}
                    </h3>
                    <span
                      className={`mt-2 inline-flex rounded-md px-2.5 py-1 text-sm font-bold ring-1 ${styles.panel}`}
                    >
                      {score.value}/{maxScore}
                    </span>
                  </div>
                  <div className="h-28 w-28">
                    <Pie data={pieChartData} options={pieChartOptions} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className="mt-6 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <p className="text-sm font-semibold uppercase text-purple-700">
            Score Breakdown
          </p>
          <h2 className="text-xl font-extrabold text-gray-900">
            Scores by section
          </h2>
        </div>
        <div className="h-80">
          <Bar data={barChartData} options={barOptions} />
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-amber-50 text-amber-700">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase text-purple-700">
                High Impact
              </p>
              <h2 className="font-extrabold text-gray-900">Weaknesses</h2>
            </div>
          </div>

          <div className="space-y-3">
            {highImpactWeaknesses.slice(0, 4).map((task) => (
              <article
                key={task.taskKey}
                className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-100"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-bold text-gray-900">
                    {task.taskLabel}
                  </p>
                  <span className="rounded-md bg-white px-2 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100">
                    {task.priority}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Accuracy {formatNumber(task.accuracy)}% | impact{" "}
                  {formatNumber(task.totalWeight)}
                </p>
              </article>
            ))}

            {highImpactWeaknesses.length === 0 && (
              <p className="rounded-md bg-slate-50 p-3 text-sm text-gray-500 ring-1 ring-slate-100">
                No high-impact weakness was detected in this attempt.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-purple-50 text-purple-700">
              <ClipboardList className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase text-purple-700">
                Practice
              </p>
              <h2 className="font-extrabold text-gray-900">Daily Plan</h2>
            </div>
          </div>

          <div className="space-y-3">
            {recommendedPractice.slice(0, 5).map((item) => (
              <article
                key={item.taskKey}
                className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-100"
              >
                <p className="text-sm font-bold text-gray-900">
                  {item.taskLabel}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {item.dailyPractice}
                </p>
              </article>
            ))}

            {recommendedPractice.length === 0 && (
              <p className="rounded-md bg-slate-50 p-3 text-sm text-gray-500 ring-1 ring-slate-100">
                Complete a full scored mock to generate practice recommendations.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-blue-50 text-blue-700">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase text-purple-700">
                Cross Scoring
              </p>
              <h2 className="font-extrabold text-gray-900">Score Losses</h2>
            </div>
          </div>

          <div className="space-y-3">
            {crossScoreLosses.slice(0, 4).map((loss) => (
              <article
                key={loss.taskKey}
                className="rounded-md bg-slate-50 p-3 ring-1 ring-slate-100"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-bold text-gray-900">
                    {loss.taskLabel}
                  </p>
                  <span className="rounded-md bg-white px-2 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-100">
                    -{formatNumber(loss.estimatedLoss)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Accuracy {formatNumber(loss.accuracy)}% | {loss.priority} priority
                </p>
              </article>
            ))}

            {crossScoreLosses.length === 0 && (
              <p className="rounded-md bg-slate-50 p-3 text-sm text-gray-500 ring-1 ring-slate-100">
                Cross-scored tasks did not create a major penalty.
              </p>
            )}
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-md bg-purple-50 text-purple-700">
            <Target className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              Question Type Breakdown
            </p>
            <h2 className="text-xl font-extrabold text-gray-900">
              Weighted task accuracy
            </h2>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-slate-200">
          <div className="grid grid-cols-[minmax(180px,1fr),90px,110px,110px] gap-3 bg-slate-50 px-4 py-3 text-xs font-bold uppercase text-gray-500 max-md:hidden">
            <span>Task</span>
            <span>Attempts</span>
            <span>Accuracy</span>
            <span>Confidence</span>
          </div>

          {taskBreakdownRows.map((task) => (
            <article
              key={task.taskKey}
              className="grid gap-2 border-t border-slate-100 px-4 py-3 first:border-t-0 md:grid-cols-[minmax(180px,1fr),90px,110px,110px] md:items-center"
            >
              <p className="text-sm font-bold text-gray-900">{task.taskLabel}</p>
              <p className="text-sm text-gray-600">{task.attempts}</p>
              <p className="text-sm font-bold text-gray-900">
                {formatNumber(task.averageScore)}%
              </p>
              <p className="text-sm text-gray-600">
                {formatNumber(task.averageConfidence)}%
              </p>
            </article>
          ))}

          {taskBreakdownRows.length === 0 && (
            <p className="p-4 text-sm text-gray-500">
              Question-level analytics are not available for this result.
            </p>
          )}
        </div>
      </section>

      <div className="mt-6 rounded-md bg-slate-50 p-4 text-sm text-gray-600 ring-1 ring-slate-100">
        <span className="font-bold text-gray-800">AI feedback:</span>{" "}
        {report.feedback?.overall ||
          "Keep reviewing the highest-impact weakness first, then retake a full mock to confirm improvement across all skills."}
      </div>
    </section>
  );
};

export default ReportCard;
